import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { AppShell } from '../../app/AppShell'
import { ApiError } from '../../api/problem'
import { useCampaignSheet } from '../../domains/campaigns/queries'
import { useCaseList } from '../../domains/cases/queries'
import { useComicList, useCreateComic } from '../../domains/comics/queries'
import { Banner, Button, Card, Input, Pentacle, SectionLabel } from '../../design/components'
import styles from './comics.module.css'

/**
 * A campaign's comics — the cases it has drawn.
 *
 * Any member may read the list; only the Game Master creates one, and that is the
 * campaign's own `isGameMaster` flag rather than anything re-derived here.
 */
export function ComicListPage() {
  const params = useParams<{ id: string }>()
  const campaignId = Number(params.id)

  const [creating, setCreating] = useState(false)
  const query = useComicList(campaignId)
  const campaign = useCampaignSheet(campaignId)

  const comics = query.data?.items ?? []
  const isGameMaster = campaign.data?.isGameMaster === true
  const campaignName = campaign.data?.sections.core?.name

  return (
    <AppShell title="Comics" subtitle={campaignName?.toUpperCase() ?? 'THE CASES, DRAWN'}>
      {query.isPending && <Card>Loading…</Card>}

      {query.isError && (
        <Card>
          <Banner tone={notFound(query.error) ? 'info' : 'error'} urgent={!notFound(query.error)}>
            {notFound(query.error)
              ? 'No such campaign, or it is not one of yours.'
              : 'Could not load this campaign’s comics.'}
          </Banner>
        </Card>
      )}

      {query.isSuccess && comics.length === 0 && !creating && (
        <Card label="No comics yet">
          <p className={styles.note}>
            {isGameMaster
              ? 'A comic retells one case in scenes and pages, with art for each.'
              : 'The Game Master has not drawn a case in this campaign yet.'}
          </p>
        </Card>
      )}

      {comics.length > 0 && (
        <ul className={styles.list}>
          {comics.map((entry) => (
            <li key={entry.id}>
              <Link to={`/comics/${entry.id}`} className={styles.row}>
                <Pentacle size={36} className={styles.sigil} />
                <span className={styles.rowText}>
                  <span className={styles.rowName}>{entry.name}</span>
                  <span className={styles.rowMeta}>
                    {[
                      entry.caseName ?? 'Unnamed case',
                      `${entry.sceneCount ?? 0} ${entry.sceneCount === 1 ? 'scene' : 'scenes'}`,
                      `${entry.pageCount ?? 0} ${entry.pageCount === 1 ? 'page' : 'pages'}`,
                    ].join(' · ')}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {isGameMaster &&
        (creating ? (
          <CreateComicCard campaignId={campaignId} onDone={() => setCreating(false)} />
        ) : (
          <Button fullWidth onClick={() => setCreating(true)}>
            New comic
          </Button>
        ))}

      <Link to={`/campaigns/${campaignId}`} className={styles.backLink}>
        <Button variant="secondary" fullWidth>
          Back to the campaign
        </Button>
      </Link>
    </AppShell>
  )
}

function notFound(error: unknown): boolean {
  return error instanceof ApiError && (error.kind === 'not-found' || error.kind === 'forbidden')
}

/**
 * A comic is created against a case, so the form reads the campaign's cases to offer them.
 *
 * There is no free-text case field and no "none" option: `caseId` is required by the
 * contract, and a comic of no case has nothing to draw.
 */
function CreateComicCard({ campaignId, onDone }: { campaignId: number; onDone: () => void }) {
  const navigate = useNavigate()
  const create = useCreateComic(campaignId)
  const cases = useCaseList(campaignId)

  const options = cases.data?.items ?? []
  const [name, setName] = useState('')
  const [caseId, setCaseId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const chosen = caseId ?? options[0]?.id ?? null

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    if (chosen === null) return

    try {
      const sheet = await create.mutateAsync({ name, caseId: chosen })
      void navigate(`/comics/${sheet.comicId}`)
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not create the comic.')
    }
  }

  return (
    <Card variant="fate" label="New comic">
      <form className={styles.form} onSubmit={(e) => void onSubmit(e)} noValidate>
        {error !== null && (
          <Banner tone="error" urgent>
            {error}
          </Banner>
        )}

        <Input
          label="Name"
          required
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <label className={styles.field}>
          <SectionLabel tone="muted" micro>
            Draws the case
          </SectionLabel>
          <select
            className={styles.select}
            value={chosen ?? ''}
            onChange={(e) => setCaseId(Number(e.target.value))}
          >
            {options.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>
        </label>

        {cases.isSuccess && options.length === 0 && (
          <p className={styles.note}>
            This campaign has no cases yet. A comic draws one, so open a case first.
          </p>
        )}

        <div className={styles.formActions}>
          <Button variant="secondary" onClick={onDone} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={create.isPending || name.trim() === '' || chosen === null}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </form>
    </Card>
  )
}
